// Source of truth for gear brands/series lives in the neighboring bc-business-scripts repo:
// src/classifiricher/static/gear-brands.ts (keep this list in sync when that file changes).
export const gearSeriesGroups = [
  {
    brandKey: "SHIMANO",
    seriesKeys: [
      // Premium to entry-level, with road, MTB, gravel, gravity, and hub lines compared loosely.
      "SHIMANO_DURA_ACE",
      "SHIMANO_XTR",
      "SHIMANO_ULTEGRA",
      "SHIMANO_DEORE_XT",
      "SHIMANO_SAINT",
      "SHIMANO_GRX",
      "SHIMANO_105",
      "SHIMANO_SLX",
      "SHIMANO_ZEE",
      "SHIMANO_DEORE",
      "SHIMANO_CUES",
      "SHIMANO_ALFINE",
      "SHIMANO_TIAGRA",
      "SHIMANO_NEXUS",
      "SHIMANO_SORA",
      "SHIMANO_CLARIS",
      "SHIMANO_ALIVIO",
      "SHIMANO_ACERA",
      "SHIMANO_ALTUS",
      "SHIMANO_ESSA",
      "SHIMANO_TOURNEY"
    ]
  },
  {
    brandKey: "SRAM",
    seriesKeys: [
      // Premium to entry-level, with road, gravel, MTB, and e-bike lines compared loosely.
      "SRAM_RED",
      "SRAM_RED_XPLR",
      "SRAM_XX_DH",
      "SRAM_XX",
      "SRAM_FORCE",
      "SRAM_FORCE_XPLR",
      "SRAM_X0",
      "SRAM_X1",
      "SRAM_GX",
      "SRAM_EAGLE_90",
      "SRAM_EAGLE_70",
      "SRAM_EAGLE_S500",
      "SRAM_EAGLE_S200",
      "SRAM_EAGLE_S100",
      "SRAM_RIVAL",
      "SRAM_RIVAL_XPLR",
      "SRAM_S1000",
      "SRAM_APEX",
      "SRAM_APEX_XPLR",
      "SRAM_EX1",
      "SRAM_NX",
      "SRAM_SX",
      "SRAM_X4"
    ]
  },
  {
    brandKey: "CAMPAGNOLO",
    seriesKeys: [
      "CAMPAGNOLO_SUPER_RECORD_13X",
      "CAMPAGNOLO_SUPER_RECORD",
      "CAMPAGNOLO_RECORD",
      "CAMPAGNOLO_EKAR",
      "CAMPAGNOLO_CHORUS",
      "CAMPAGNOLO_POTENZA",
      "CAMPAGNOLO_CENTAUR"
    ]
  },
  { brandKey: "ROHLOFF", seriesKeys: ["ROHLOFF_SPEEDHUB"] },
  { brandKey: "PINION", seriesKeys: ["PINION_MGU", "PINION_P_LINE", "PINION_C_LINE"] },
  { brandKey: "BAFANG", seriesKeys: ["BAFANG_H730"] },
  {
    brandKey: "ENVIOLO",
    seriesKeys: [
      "ENVIOLO_EXTREME",
      "ENVIOLO_HEAVY_DUTY",
      "ENVIOLO_SPORTIVE",
      "ENVIOLO_TREKKING",
      "ENVIOLO_CITY",
      "ENVIOLO_UTILITY",
      "ENVIOLO_URBAN"
    ]
  },
  {
    brandKey: "MICROSHIFT",
    seriesKeys: ["MICROSHIFT_CENTOS", "MICROSHIFT_SWORD", "MICROSHIFT_ADVENT", "MICROSHIFT_ACOLYTE"]
  },
  { brandKey: "SUNRACE", seriesKeys: ["SUNRACE_GROUPSET"] },
  { brandKey: "STURMEY_ARCHER", seriesKeys: ["STURMEY_ARCHER_IGH"] },
  {
    brandKey: "TRP",
    seriesKeys: ["TRP_VISTAR", "TRP_EVO_12", "TRP_EVO_7_DH", "TRP_TR12", "TRP_EASI_10_11"]
  },
  { brandKey: "TEKTRO", seriesKeys: ["TEKTRO_ED9", "TEKTRO_ED8", "TEKTRO_GEMINI"] },
  { brandKey: "THREE_BY_THREE", seriesKeys: ["THREE_BY_THREE_NINE"] },
  { brandKey: "HOPE", seriesKeys: ["HOPE_HB"] },
  { brandKey: "UNKNOWN", seriesKeys: ["UNKNOWN"] }
] as const;

export type GearSeriesGroupDefinition = (typeof gearSeriesGroups)[number];
export type GearBrandKey = GearSeriesGroupDefinition["brandKey"];
export type GearSeriesKey = GearSeriesGroupDefinition["seriesKeys"][number];

export const gearBrandKeys = gearSeriesGroups.map(({ brandKey }) => brandKey) as GearBrandKey[];

export const gearSeriesKeys = gearSeriesGroups.flatMap(({ seriesKeys }) => seriesKeys) as GearSeriesKey[];

export const gearSeriesToBrand = new Map<string, GearBrandKey>(
  gearSeriesGroups.flatMap(group =>
    group.seriesKeys.map((seriesKey): [string, GearBrandKey] => [seriesKey, group.brandKey])
  )
);

export type GearUseCase = "mountain" | "road" | "gravel" | "city";
export type GearQualityTier = "entryMid" | "upper" | "highEnd";
export const gearUseCases = ["mountain", "road", "gravel", "city"] as const satisfies readonly GearUseCase[];
export const gearQualityTiers = ["entryMid", "upper", "highEnd"] as const satisfies readonly GearQualityTier[];
export type GearSeriesMatrix = Partial<Record<GearUseCase, Partial<Record<GearQualityTier, readonly GearSeriesKey[]>>>>;

// TODO: Move this partner-specific presentation data to the brands content package once supported by bc-business-scripts.
export const gearSeriesMatrices = {
  SHIMANO: {
    mountain: {
      entryMid: ["SHIMANO_DEORE", "SHIMANO_CUES"],
      upper: ["SHIMANO_DEORE_XT"],
      highEnd: ["SHIMANO_XTR", "SHIMANO_SAINT"]
    },
    road: {
      entryMid: ["SHIMANO_TIAGRA", "SHIMANO_CUES"],
      upper: ["SHIMANO_105", "SHIMANO_ULTEGRA"],
      highEnd: ["SHIMANO_DURA_ACE"]
    },
    gravel: {
      entryMid: ["SHIMANO_ESSA", "SHIMANO_CUES"],
      upper: ["SHIMANO_GRX"]
    },
    city: {
      entryMid: ["SHIMANO_ESSA", "SHIMANO_NEXUS"],
      upper: ["SHIMANO_CUES", "SHIMANO_ALFINE"]
    }
  }
} as const satisfies Partial<Record<GearBrandKey, GearSeriesMatrix>>;

export const getGearBrandKeyForSeries = (seriesKey: string): GearBrandKey => {
  return gearSeriesToBrand.get(seriesKey) ?? "UNKNOWN";
};

export const getGearSeriesMatrix = (brandKey: GearBrandKey): GearSeriesMatrix | undefined => {
  return gearSeriesMatrices[brandKey as keyof typeof gearSeriesMatrices];
};

/**
 * Returns catalog series that are not assigned to the brand's presentation matrix, so the UI can list them separately.
 */
export const getUnplacedGearSeriesKeys = (brandKey: GearBrandKey): GearSeriesKey[] => {
  const seriesKeys = gearSeriesGroups.find(group => group.brandKey === brandKey)?.seriesKeys ?? [];
  const matrix = getGearSeriesMatrix(brandKey);
  const placedSeriesKeys = new Set<string>(
    Object.values(matrix ?? {}).flatMap(tiers => Object.values(tiers).flatMap(keys => keys ?? []))
  );

  return seriesKeys.filter(seriesKey => !placedSeriesKeys.has(seriesKey));
};
